import { useEffect, useRef, useState } from "react";

import InlineError from "../../../shared/ui/Field/InlineError";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";
import {
  COMPANY_FINANCE_CURRENCIES,
  CORRECTION_TARGET_KIND_LABELS,
  COUNTERPARTY_KIND_LABELS,
  formatCompanyFinanceMoney,
  kyivTodayISO,
  mapCompanyFinanceError,
} from "../lib/companyFinanceLabels";

function useFinanceDialogChrome({ open, onClose, submittingRef, focusRef }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    const csection = document.getElementById("Csection");
    const previousCsection = csection ? csection.style.overflow : "";
    document.body.style.overflow = "hidden";
    if (csection) csection.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      if (focusRef?.current) focusRef.current.focus();
    }, 0);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (submittingRef.current) return;
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (csection) csection.style.overflow = previousCsection;
    };
  }, [open, focusRef, submittingRef]);

  return { dialogRef, onCloseRef };
}

function DialogShell({
  open,
  title,
  titleId,
  testId,
  onClose,
  submitting,
  dialogRef,
  children,
}) {
  if (!open) return null;
  return (
    <div
      className="feature-company-finance__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid={testId}
      data-modal-layer="application"
      data-submitting={submitting ? "true" : "false"}
      ref={dialogRef}
    >
      <div
        className="feature-company-finance__dialog-backdrop"
        aria-hidden="true"
        onClick={() => {
          if (!submitting) onClose();
        }}
      />
      <div className="feature-company-finance__dialog-panel">
        <div className="feature-company-finance__dialog-header">
          <h2 id={titleId}>{title}</h2>
          <button
            type="button"
            className="action-btn feature-company-finance__dialog-close"
            onClick={onClose}
            aria-label="Закрити"
            disabled={submitting}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function CreateReceiptDialog({ open, onClose, onSubmit, writesSuspended = false }) {
  const submittingRef = useRef(false);
  const firstFieldRef = useRef(null);
  const { dialogRef } = useFinanceDialogChrome({
    open,
    onClose,
    submittingRef,
    focusRef: firstFieldRef,
  });
  const [draft, setDraft] = useState({
    amount: "",
    currency: "UAH",
    received_on: kyivTodayISO(),
    note: "",
    deal_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setDraft({
      amount: "",
      currency: "UAH",
      received_on: kyivTodayISO(),
      note: "",
      deal_id: "",
    });
    setFormError("");
    setFieldErrors({});
    setSubmitting(false);
    submittingRef.current = false;
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (writesSuspended || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");
    setFieldErrors({});
    const body = {
      amount: draft.amount,
      currency: draft.currency,
      received_on: draft.received_on,
    };
    if (draft.note) body.note = draft.note;
    if (draft.deal_id !== "") {
      const parsed = Number(draft.deal_id);
      if (Number.isFinite(parsed)) body.deal_id = parsed;
    }
    try {
      const outcome = await onSubmit(body);
      // Close only on explicit accepted success — stale/duplicate are silent.
      if (outcome?.accepted === true) {
        onClose();
      }
    } catch (error) {
      const mapped = mapCompanyFinanceError(error);
      const parsed = parseDrfFieldErrors(error, [
        "amount",
        "currency",
        "received_on",
        "note",
        "deal_id",
      ]);
      setFieldErrors(parsed);
      setFormError(
        mapped.message ||
          parsed._form ||
          messageFromError(error) ||
          "Не вдалося створити надходження.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      open={open}
      title="Нове надходження"
      titleId="company-finance-receipt-title"
      testId="company-finance-create-receipt"
      onClose={() => {
        if (!submittingRef.current) onClose();
      }}
      submitting={submitting}
      dialogRef={dialogRef}
    >
      <form onSubmit={handleSubmit} noValidate>
        <label className="feature-company-finance__field" htmlFor="cf-receipt-amount">
          <span>Сума</span>
          <input
            id="cf-receipt-amount"
            ref={firstFieldRef}
            type="text"
            inputMode="decimal"
            value={draft.amount}
            onChange={(e) => setDraft((p) => ({ ...p, amount: e.target.value }))}
            required
            disabled={submitting}
            aria-invalid={Boolean(fieldErrors.amount)}
          />
          {fieldErrors.amount ? <InlineError>{fieldErrors.amount}</InlineError> : null}
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-receipt-currency">
          <span>Валюта</span>
          <select
            id="cf-receipt-currency"
            value={draft.currency}
            onChange={(e) => setDraft((p) => ({ ...p, currency: e.target.value }))}
            disabled={submitting}
          >
            {COMPANY_FINANCE_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-receipt-received-on">
          <span>Дата отримання</span>
          <input
            id="cf-receipt-received-on"
            type="date"
            value={draft.received_on}
            onChange={(e) => setDraft((p) => ({ ...p, received_on: e.target.value }))}
            required
            disabled={submitting}
          />
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-receipt-note">
          <span>Нотатка (необовʼязково)</span>
          <textarea
            id="cf-receipt-note"
            rows={2}
            value={draft.note}
            onChange={(e) => {
              setDraft((p) => ({ ...p, note: e.target.value }));
              setFieldErrors((prev) => {
                if (!prev?.note) return prev;
                const next = { ...prev };
                delete next.note;
                return next;
              });
            }}
            disabled={submitting}
            aria-invalid={Boolean(fieldErrors.note)}
          />
          {fieldErrors.note ? <InlineError>{fieldErrors.note}</InlineError> : null}
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-receipt-deal">
          <span>ID угоди (необовʼязково)</span>
          <input
            id="cf-receipt-deal"
            type="text"
            inputMode="numeric"
            value={draft.deal_id}
            onChange={(e) => setDraft((p) => ({ ...p, deal_id: e.target.value }))}
            disabled={submitting}
          />
        </label>
        {formError ? (
          <p className="feature-company-finance__form-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="feature-company-finance__dialog-actions">
          <button type="submit" className="action-btn action-btn--primary" disabled={submitting || writesSuspended}>
            {submitting ? "Збереження…" : "Створити надходження"}
          </button>
          <button
            type="button"
            className="action-btn"
            onClick={() => {
              if (!submittingRef.current) onClose();
            }}
            disabled={submitting}
          >
            Скасувати
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

export function CreateObligationDialog({
  open,
  onClose,
  onSubmit,
  members = [],
  writesSuspended = false,
}) {
  const submittingRef = useRef(false);
  const firstFieldRef = useRef(null);
  const { dialogRef } = useFinanceDialogChrome({
    open,
    onClose,
    submittingRef,
    focusRef: firstFieldRef,
  });
  const [draft, setDraft] = useState({
    counterparty_kind: "employee",
    member_id: "",
    amount: "",
    currency: "UAH",
    due_on: kyivTodayISO(),
    deal_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setDraft({
      counterparty_kind: "employee",
      member_id: "",
      amount: "",
      currency: "UAH",
      due_on: kyivTodayISO(),
      deal_id: "",
    });
    setFormError("");
    setFieldErrors({});
    setSubmitting(false);
    submittingRef.current = false;
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (writesSuspended || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");
    setFieldErrors({});
    const body = {
      counterparty_kind: draft.counterparty_kind,
      amount: draft.amount,
      currency: draft.currency,
      due_on: draft.due_on,
    };
    if (draft.counterparty_kind === "employee" && draft.member_id !== "") {
      const parsed = Number(draft.member_id);
      if (Number.isFinite(parsed)) body.member_id = parsed;
    }
    if (draft.deal_id !== "") {
      const parsed = Number(draft.deal_id);
      if (Number.isFinite(parsed)) body.deal_id = parsed;
    }
    try {
      const outcome = await onSubmit(body);
      // Close only on explicit accepted success — stale/duplicate are silent.
      if (outcome?.accepted === true) {
        onClose();
      }
    } catch (error) {
      const mapped = mapCompanyFinanceError(error);
      const parsed = parseDrfFieldErrors(error, [
        "amount",
        "currency",
        "due_on",
        "counterparty_kind",
        "member_id",
        "deal_id",
      ]);
      setFieldErrors(parsed);
      setFormError(
        mapped.message ||
          parsed._form ||
          messageFromError(error) ||
          "Не вдалося створити зобов'язання.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      open={open}
      title="Нове зобов'язання"
      titleId="company-finance-obligation-title"
      testId="company-finance-create-obligation"
      onClose={() => {
        if (!submittingRef.current) onClose();
      }}
      submitting={submitting}
      dialogRef={dialogRef}
    >
      <form onSubmit={handleSubmit} noValidate>
        <label className="feature-company-finance__field" htmlFor="cf-ob-kind">
          <span>Контрагент</span>
          <select
            id="cf-ob-kind"
            ref={firstFieldRef}
            value={draft.counterparty_kind}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                counterparty_kind: e.target.value,
                member_id: e.target.value === "employee" ? p.member_id : "",
              }))
            }
            disabled={submitting}
          >
            {Object.entries(COUNTERPARTY_KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {draft.counterparty_kind === "employee" ? (
          <label className="feature-company-finance__field" htmlFor="cf-ob-member">
            <span>Учасник компанії</span>
            <select
              id="cf-ob-member"
              value={draft.member_id}
              onChange={(e) => setDraft((p) => ({ ...p, member_id: e.target.value }))}
              required
              disabled={submitting}
            >
              <option value="">Оберіть учасника</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.user?.fullname ||
                    member.user?.handle ||
                    member.user?.email ||
                    `Учасник #${member.id}`}
                </option>
              ))}
            </select>
            {fieldErrors.member_id ? (
              <InlineError>{fieldErrors.member_id}</InlineError>
            ) : null}
          </label>
        ) : null}
        <label className="feature-company-finance__field" htmlFor="cf-ob-amount">
          <span>Сума</span>
          <input
            id="cf-ob-amount"
            type="text"
            inputMode="decimal"
            value={draft.amount}
            onChange={(e) => setDraft((p) => ({ ...p, amount: e.target.value }))}
            required
            disabled={submitting}
          />
          {fieldErrors.amount ? <InlineError>{fieldErrors.amount}</InlineError> : null}
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-ob-currency">
          <span>Валюта</span>
          <select
            id="cf-ob-currency"
            value={draft.currency}
            onChange={(e) => setDraft((p) => ({ ...p, currency: e.target.value }))}
            disabled={submitting}
          >
            {COMPANY_FINANCE_CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-ob-due">
          <span>Термін</span>
          <input
            id="cf-ob-due"
            type="date"
            value={draft.due_on}
            onChange={(e) => setDraft((p) => ({ ...p, due_on: e.target.value }))}
            required
            disabled={submitting}
          />
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-ob-deal">
          <span>ID угоди (необовʼязково)</span>
          <input
            id="cf-ob-deal"
            type="text"
            inputMode="numeric"
            value={draft.deal_id}
            onChange={(e) => setDraft((p) => ({ ...p, deal_id: e.target.value }))}
            disabled={submitting}
          />
        </label>
        {formError ? (
          <p className="feature-company-finance__form-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="feature-company-finance__dialog-actions">
          <button type="submit" className="action-btn action-btn--primary" disabled={submitting || writesSuspended}>
            {submitting ? "Збереження…" : "Створити зобов'язання"}
          </button>
          <button
            type="button"
            className="action-btn"
            onClick={() => {
              if (!submittingRef.current) onClose();
            }}
            disabled={submitting}
          >
            Скасувати
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

export function CreateAllocationDialog({
  open,
  onClose,
  onSubmit,
  receipts = [],
  obligations = [],
  writesSuspended = false,
}) {
  const submittingRef = useRef(false);
  const firstFieldRef = useRef(null);
  const { dialogRef } = useFinanceDialogChrome({
    open,
    onClose,
    submittingRef,
    focusRef: firstFieldRef,
  });
  const [draft, setDraft] = useState({
    receipt_id: "",
    obligation_id: "",
    amount: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDraft({ receipt_id: "", obligation_id: "", amount: "" });
    setFormError("");
    setSubmitting(false);
    submittingRef.current = false;
  }, [open]);

  const selectedReceipt = receipts.find(
    (row) => String(row.id) === String(draft.receipt_id),
  );
  const compatibleObligations = selectedReceipt
    ? obligations.filter((row) => row.currency === selectedReceipt.currency)
    : obligations;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (writesSuspended || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");
    const body = {
      receipt_id: Number(draft.receipt_id),
      obligation_id: Number(draft.obligation_id),
      amount: draft.amount,
    };
    try {
      const outcome = await onSubmit(body);
      // Close only on explicit accepted success — stale/duplicate are silent.
      if (outcome?.accepted === true) {
        onClose();
      }
    } catch (error) {
      const mapped = mapCompanyFinanceError(error);
      setFormError(
        mapped.message ||
          messageFromError(error) ||
          "Не вдалося створити розподіл коштів.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      open={open}
      title="Резерв / розподіл коштів"
      titleId="company-finance-allocation-title"
      testId="company-finance-create-allocation"
      onClose={() => {
        if (!submittingRef.current) onClose();
      }}
      submitting={submitting}
      dialogRef={dialogRef}
    >
      <p className="feature-company-finance__dialog-hint">
        Розподіл — це резерв коштів з надходження під зобовʼязання. Це не виплата.
      </p>
      <form onSubmit={handleSubmit} noValidate>
        <label className="feature-company-finance__field" htmlFor="cf-al-receipt">
          <span>Надходження</span>
          <select
            id="cf-al-receipt"
            ref={firstFieldRef}
            value={draft.receipt_id}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                receipt_id: e.target.value,
                obligation_id: "",
              }))
            }
            required
            disabled={submitting}
          >
            <option value="">Оберіть надходження</option>
            {receipts.map((row) => (
              <option key={row.id} value={row.id}>
                #{row.id} · {formatCompanyFinanceMoney(row.unallocated_amount, row.currency)}{" "}
                вільно
              </option>
            ))}
          </select>
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-al-obligation">
          <span>Зобовʼязання</span>
          <select
            id="cf-al-obligation"
            value={draft.obligation_id}
            onChange={(e) => setDraft((p) => ({ ...p, obligation_id: e.target.value }))}
            required
            disabled={submitting || !draft.receipt_id}
          >
            <option value="">Оберіть зобовʼязання</option>
            {compatibleObligations.map((row) => (
              <option key={row.id} value={row.id}>
                #{row.id} · {formatCompanyFinanceMoney(row.outstanding, row.currency)} залишок
              </option>
            ))}
          </select>
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-al-amount">
          <span>Сума резерву</span>
          <input
            id="cf-al-amount"
            type="text"
            inputMode="decimal"
            value={draft.amount}
            onChange={(e) => setDraft((p) => ({ ...p, amount: e.target.value }))}
            required
            disabled={submitting}
          />
        </label>
        {formError ? (
          <p className="feature-company-finance__form-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="feature-company-finance__dialog-actions">
          <button type="submit" className="action-btn action-btn--primary" disabled={submitting || writesSuspended}>
            {submitting ? "Збереження…" : "Зарезервувати"}
          </button>
          <button
            type="button"
            className="action-btn"
            onClick={() => {
              if (!submittingRef.current) onClose();
            }}
            disabled={submitting}
          >
            Скасувати
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

export function CreateSettlementDialog({
  open,
  onClose,
  onSubmit,
  obligations = [],
  writesSuspended = false,
}) {
  const submittingRef = useRef(false);
  const firstFieldRef = useRef(null);
  const { dialogRef } = useFinanceDialogChrome({
    open,
    onClose,
    submittingRef,
    focusRef: firstFieldRef,
  });
  const [draft, setDraft] = useState({
    obligation_id: "",
    amount: "",
    settled_on: kyivTodayISO(),
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!open) return;
    setDraft({
      obligation_id: "",
      amount: "",
      settled_on: kyivTodayISO(),
    });
    setFormError("");
    setSubmitting(false);
    submittingRef.current = false;
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (writesSuspended || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");
    const body = {
      obligation_id: Number(draft.obligation_id),
      amount: draft.amount,
      settled_on: draft.settled_on,
    };
    try {
      const outcome = await onSubmit(body);
      // Close only on explicit accepted success — stale/duplicate are silent.
      if (outcome?.accepted === true) {
        onClose();
      }
    } catch (error) {
      const mapped = mapCompanyFinanceError(error);
      setFormError(
        mapped.message ||
          messageFromError(error) ||
          "Не вдалося зафіксувати виплату.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      open={open}
      title="Нова виплата"
      titleId="company-finance-settlement-title"
      testId="company-finance-create-settlement"
      onClose={() => {
        if (!submittingRef.current) onClose();
      }}
      submitting={submitting}
      dialogRef={dialogRef}
    >
      <p className="feature-company-finance__dialog-hint">
        Виплата фіксує фактичне списання коштів. Часткова виплата залишає зобовʼязання відкритим.
      </p>
      <form onSubmit={handleSubmit} noValidate>
        <label className="feature-company-finance__field" htmlFor="cf-st-obligation">
          <span>Зобовʼязання</span>
          <select
            id="cf-st-obligation"
            ref={firstFieldRef}
            value={draft.obligation_id}
            onChange={(e) => setDraft((p) => ({ ...p, obligation_id: e.target.value }))}
            required
            disabled={submitting}
          >
            <option value="">Оберіть зобовʼязання</option>
            {obligations.map((row) => (
              <option key={row.id} value={row.id}>
                #{row.id} · {formatCompanyFinanceMoney(row.outstanding, row.currency)} залишок
              </option>
            ))}
          </select>
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-st-amount">
          <span>Сума виплати</span>
          <input
            id="cf-st-amount"
            type="text"
            inputMode="decimal"
            value={draft.amount}
            onChange={(e) => setDraft((p) => ({ ...p, amount: e.target.value }))}
            required
            disabled={submitting}
          />
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-st-date">
          <span>Дата виплати</span>
          <input
            id="cf-st-date"
            type="date"
            value={draft.settled_on}
            onChange={(e) => setDraft((p) => ({ ...p, settled_on: e.target.value }))}
            required
            disabled={submitting}
          />
        </label>
        {formError ? (
          <p className="feature-company-finance__form-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="feature-company-finance__dialog-actions">
          <button type="submit" className="action-btn action-btn--primary" disabled={submitting || writesSuspended}>
            {submitting ? "Збереження…" : "Зафіксувати виплату"}
          </button>
          <button
            type="button"
            className="action-btn"
            onClick={() => {
              if (!submittingRef.current) onClose();
            }}
            disabled={submitting}
          >
            Скасувати
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

export function CreateCorrectionDialog({
  open,
  onClose,
  onSubmit,
  receipts = [],
  obligations = [],
  allocations = [],
  settlements = [],
  writesSuspended = false,
}) {
  const submittingRef = useRef(false);
  const firstFieldRef = useRef(null);
  const { dialogRef } = useFinanceDialogChrome({
    open,
    onClose,
    submittingRef,
    focusRef: firstFieldRef,
  });
  const [draft, setDraft] = useState({
    target_kind: "receipt",
    target_id: "",
    delta_amount: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setDraft({
      target_kind: "receipt",
      target_id: "",
      delta_amount: "",
      reason: "",
    });
    setFormError("");
    setFieldErrors({});
    setSubmitting(false);
    submittingRef.current = false;
  }, [open]);

  const targetOptions = (() => {
    if (draft.target_kind === "receipt") return receipts;
    if (draft.target_kind === "obligation") return obligations;
    if (draft.target_kind === "allocation") return allocations;
    return settlements;
  })();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (writesSuspended || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");
    setFieldErrors({});
    const body = {
      target_kind: draft.target_kind,
      target_id: Number(draft.target_id),
      delta_amount: draft.delta_amount,
      reason: draft.reason,
    };
    try {
      const outcome = await onSubmit(body);
      // Close only on explicit accepted success — stale/duplicate are silent.
      if (outcome?.accepted === true) {
        onClose();
      }
    } catch (error) {
      const mapped = mapCompanyFinanceError(error);
      const parsed = parseDrfFieldErrors(error, [
        "target_kind",
        "target_id",
        "delta_amount",
        "reason",
      ]);
      setFieldErrors(parsed);
      setFormError(
        mapped.message ||
          parsed._form ||
          messageFromError(error) ||
          "Не вдалося додати коригування.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <DialogShell
      open={open}
      title="Коригування"
      titleId="company-finance-correction-title"
      testId="company-finance-create-correction"
      onClose={() => {
        if (!submittingRef.current) onClose();
      }}
      submitting={submitting}
      dialogRef={dialogRef}
    >
      <p className="feature-company-finance__dialog-hint">
        Коригування додається окремим записом і не редагує початкову суму.
        Позитивне значення збільшує ефективну суму; відʼємне — зменшує.
      </p>
      <form onSubmit={handleSubmit} noValidate>
        <label className="feature-company-finance__field" htmlFor="cf-cr-kind">
          <span>Ціль</span>
          <select
            id="cf-cr-kind"
            ref={firstFieldRef}
            value={draft.target_kind}
            onChange={(e) =>
              setDraft((p) => ({
                ...p,
                target_kind: e.target.value,
                target_id: "",
              }))
            }
            disabled={submitting}
          >
            {Object.entries(CORRECTION_TARGET_KIND_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-cr-target">
          <span>Запис</span>
          <select
            id="cf-cr-target"
            value={draft.target_id}
            onChange={(e) => setDraft((p) => ({ ...p, target_id: e.target.value }))}
            required
            disabled={submitting}
          >
            <option value="">Оберіть запис</option>
            {targetOptions.map((row) => (
              <option key={row.id} value={row.id}>
                #{row.id} · {formatCompanyFinanceMoney(row.amount ?? row.effective_amount, row.currency)}
              </option>
            ))}
          </select>
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-cr-delta">
          <span>Дельта (наприклад +50.00 або -50.00)</span>
          <input
            id="cf-cr-delta"
            type="text"
            inputMode="decimal"
            value={draft.delta_amount}
            onChange={(e) => setDraft((p) => ({ ...p, delta_amount: e.target.value }))}
            required
            disabled={submitting}
          />
        </label>
        <label className="feature-company-finance__field" htmlFor="cf-cr-reason">
          <span>Причина</span>
          <textarea
            id="cf-cr-reason"
            rows={3}
            value={draft.reason}
            onChange={(e) => {
              setDraft((p) => ({ ...p, reason: e.target.value }));
              setFieldErrors((prev) => {
                if (!prev?.reason) return prev;
                const next = { ...prev };
                delete next.reason;
                return next;
              });
            }}
            required
            disabled={submitting}
            aria-invalid={Boolean(fieldErrors.reason)}
          />
          {fieldErrors.reason ? (
            <InlineError>{fieldErrors.reason}</InlineError>
          ) : null}
        </label>
        {formError ? (
          <p className="feature-company-finance__form-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="feature-company-finance__dialog-actions">
          <button type="submit" className="action-btn action-btn--primary" disabled={submitting || writesSuspended}>
            {submitting ? "Збереження…" : "Додати коригування"}
          </button>
          <button
            type="button"
            className="action-btn"
            onClick={() => {
              if (!submittingRef.current) onClose();
            }}
            disabled={submitting}
          >
            Скасувати
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
